import {
  formatPercentage,
  formatTaxRate,
} from '@airship-platform/airship-patterns';
import { FlexBox, ItemSelector } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Fragment } from 'react/jsx-runtime';
import { z } from 'zod';

export const NO_FINANCING_ID = 'NO_FINANCING_AVAILABLE';

interface AppointmentProposalOptionFinancingConfig {
  name: string;
  appointmentId: string;
  appointmentProposalId: string;
  appointmentProposalOptionId: string;
  financingPlanId?: string;
  optionFinancingDisabled?: boolean;
  availableFinancingPlanIds?: string[];
}

interface FinancingConfig {
  accountingId: string;
  name?: string;
  merchantFee?: number;
  promoEnabled: boolean;
  financingConfig: {
    interestRate: number;
    termMonths: number;
  };
  financingConfigPromo?: {
    promoTermMonths?: number;
    promoInterestRate?: number;
    promoNoPaymentEnabled?: boolean;
  };
}

type FinancingSelectorProps = {
  options: AppointmentProposalOptionFinancingConfig[];
  financingConfigs: FinancingConfig[];
  defaultValues: FinancingInputs;
  formId?: string;
  onSubmit?: (data: FinancingInputs) => void;
  onChange?: (data: FinancingInputs) => void;
};

const FinancingSchema = z.object({
  options: z
    .object({
      appointmentId: z.uuid(),
      appointmentProposalId: z.uuid(),
      appointmentProposalOptionId: z.uuid(),
      financingId: z.uuid().optional().nullable(),
      optionFinancingDisabled: z.boolean().optional(),
      availableFinancingPlanIds: z.array(z.uuid()).optional(),
    })
    .array(),
});

export type FinancingInputs = z.infer<typeof FinancingSchema>;

const FinancingSelector: React.FC<FinancingSelectorProps> = ({
  options,
  financingConfigs,
  defaultValues,
  formId,
  onSubmit,
  onChange,
}) => {
  const [selectedOption, setSelectedOption] =
    useState<AppointmentProposalOptionFinancingConfig>(options[0]);

  const { formState, handleSubmit, watch, setValue, reset, getValues } =
    useForm<FinancingInputs>({
      resolver: zodResolver(FinancingSchema),
      defaultValues,
    });

  const formOptions = watch('options');
  const selectedOptionIndex = useMemo(
    () =>
      formOptions.findIndex(
        (o) =>
          o.appointmentProposalOptionId ===
          selectedOption?.appointmentProposalOptionId
      ),
    [formOptions, selectedOption]
  );

  const selectedFormOption = formOptions[selectedOptionIndex];

  const optionHasChanged = !!formState.isDirty;

  const getPromoLabel = (finance: FinancingConfig): string => {
    if (!finance.promoEnabled) {
      return 'No promotional period';
    }

    const months = finance.financingConfigPromo?.promoTermMonths ?? 0;
    const monthsText = months === 1 ? 'month' : `${months} months`;

    if (finance.financingConfigPromo?.promoNoPaymentEnabled) {
      return `$0 for first ${monthsText}`;
    }

    return `${formatPercentage(
      finance.financingConfigPromo?.promoInterestRate ?? 0
    )} for first ${monthsText}`;
  };

  const handleSelectedOptionChange = (
    option: AppointmentProposalOptionFinancingConfig
  ) => {
    setSelectedOption(option);
    reset(getValues());
  };

  const isFinancingOptionSelected = (accountingId: string) => {
    if (!selectedFormOption) {
      return false;
    }

    if (accountingId === NO_FINANCING_ID) {
      return selectedFormOption.optionFinancingDisabled === true;
    }

    return (
      selectedFormOption.availableFinancingPlanIds === undefined ||
      selectedFormOption.availableFinancingPlanIds?.includes(accountingId)
    );
  };

  const isFinancingOptionDefault = (accountingId: string) => {
    if (!selectedFormOption) {
      return false;
    }

    return selectedFormOption.financingId === accountingId;
  };

  const handleFinancingChange = (accountingId: string, checked: boolean) => {
    if (selectedOptionIndex === -1) return;

    if (accountingId === NO_FINANCING_ID) {
      setValue(
        `options.${selectedOptionIndex}.optionFinancingDisabled`,
        checked,
        { shouldDirty: true }
      );
    } else {
      if (checked) {
        setValue(
          `options.${selectedOptionIndex}.optionFinancingDisabled`,
          false,
          { shouldDirty: true }
        );
      }
      const current =
        formOptions[selectedOptionIndex].availableFinancingPlanIds ||
        financingConfigs.map((config) => config.accountingId);
      const updated = checked
        ? [...new Set([accountingId, ...current])]
        : current.filter((id) => id !== accountingId);
      setValue(
        `options.${selectedOptionIndex}.availableFinancingPlanIds`,
        updated,
        { shouldDirty: true }
      );
    }

    if (onChange) {
      handleSubmit(onChange)();
    }
  };

  const handleDefaultChange = (accountingId: string) => {
    if (selectedOptionIndex === -1) return;

    setValue(`options.${selectedOptionIndex}.financingId`, accountingId, {
      shouldDirty: true,
    });
    handleFinancingChange(accountingId, true);
  };

  const handleApplyToAll = () => {
    formOptions.forEach((option, index) => {
      setValue(
        `options.${index}.financingId`,
        formOptions[selectedOptionIndex].financingId
      );
      setValue(
        `options.${index}.optionFinancingDisabled`,
        formOptions[selectedOptionIndex].optionFinancingDisabled
      );
      setValue(
        `options.${index}.availableFinancingPlanIds`,
        formOptions[selectedOptionIndex].availableFinancingPlanIds
      );
    });
    reset(getValues());

    if (onChange) {
      handleSubmit(onChange)();
    }
  };

  return (
    <Fragment>
      <FlexBox width="100%" gap={1} overflow={'hidden'} mb={2}>
        {options.map((option) => (
          <ItemSelector
            key={option.appointmentProposalOptionId}
            label={option.name}
            value={option.appointmentProposalOptionId}
            checked={
              selectedOption.appointmentProposalOptionId ===
              option.appointmentProposalOptionId
            }
            type="radio"
            handleChange={() => handleSelectedOptionChange(option)}
          />
        ))}
      </FlexBox>
      <FlexBox
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        boxSizing="border-box"
        borderRadius={2}
        width="100%"
        px={2}
        py={2}
        border={(theme) => `1px solid ${theme.palette.gray?.[300]}`}
      >
        <Typography variant="body2">
          Apply this setup to all other proposal options
        </Typography>
        <Button
          color="info"
          onClick={handleApplyToAll}
          disabled={!optionHasChanged}
        >
          Apply to all options
        </Button>
      </FlexBox>
      <Box
        component={'form'}
        id={formId}
        onSubmit={onSubmit && handleSubmit(onSubmit)}
        autoComplete="off"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
        }}
      >
        <FlexBox flexDirection="column" width="100%">
          {financingConfigs.map((config) => (
            <Fragment key={`default-${config.accountingId}`}>
              <FlexBox
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                py={2}
              >
                <FlexBox flexDirection="column" width="30%">
                  <Typography variant="subtitle1">
                    {formatTaxRate(config.financingConfig.interestRate)}% for{' '}
                    {config.financingConfig.termMonths} months
                  </Typography>
                  <Typography variant="body2">
                    {config.name} &bull; {getPromoLabel(config)}
                    {config.merchantFee != null &&
                      ` · Merchant fee ${config.merchantFee}%`}
                  </Typography>
                </FlexBox>
                <FlexBox flexDirection="row" alignItems="center" gap={1.5}>
                  <Button
                    variant="outlined"
                    color="gray"
                    onClick={() => handleDefaultChange(config.accountingId)}
                    disabled={
                      isFinancingOptionDefault(config.accountingId) ||
                      isFinancingOptionSelected(NO_FINANCING_ID)
                    }
                  >
                    {isFinancingOptionDefault(config.accountingId)
                      ? 'Default'
                      : 'Set as default'}
                  </Button>
                  <Switch
                    onChange={(event) =>
                      handleFinancingChange(
                        config.accountingId,
                        event.target.checked
                      )
                    }
                    checked={isFinancingOptionSelected(config.accountingId)}
                    disabled={
                      isFinancingOptionDefault(config.accountingId) ||
                      isFinancingOptionSelected(NO_FINANCING_ID)
                    }
                    color="info"
                  />
                </FlexBox>
              </FlexBox>
              <Divider />
            </Fragment>
          ))}
          <FlexBox
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            py={2}
          >
            <FlexBox flexDirection="column" width="30%">
              <Typography variant="body2">No financing available</Typography>
            </FlexBox>
            <FlexBox flexDirection="row" alignItems="center" gap={1.5}>
              <Switch
                color="info"
                onChange={(event) =>
                  handleFinancingChange(NO_FINANCING_ID, event.target.checked)
                }
                checked={isFinancingOptionSelected(NO_FINANCING_ID)}
              />
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </Box>
    </Fragment>
  );
};

export default FinancingSelector;
